import { forwardRef, type PropsWithChildren, useMemo } from "react";

import { cn } from "$/lib/utils/functions/misc.functions";

import CarouselNavigationButton from "../../atoms/CarouselNavigationButton/CarouselNavigationButton";
import { useNavigationButton } from "../../atoms/CarouselNavigationButton/CarouselNavigationButton.hooks";
import type { CarouselNavigationProps } from "./CarouselNavigation.types";

const CarouselNavigation = forwardRef<
  HTMLDivElement,
  PropsWithChildren<CarouselNavigationProps>
>(({ emblaApi, carouselType, numberOfNavButtonsToDisplay }, forwardedRef) => {
  const { selectedIndex, scrollSnaps, onNavigationButtonClick } =
    useNavigationButton(emblaApi);

  const relevantNavButtons = useMemo(() => {
    const totalSlides = scrollSnaps.length;
    const snaps = scrollSnaps.map((_, index) => index);

    if (selectedIndex < numberOfNavButtonsToDisplay)
      return snaps.slice(0, numberOfNavButtonsToDisplay);

    const start =
      Math.floor(selectedIndex / numberOfNavButtonsToDisplay) *
      numberOfNavButtonsToDisplay;
    return snaps.slice(
      start,
      Math.min(start + numberOfNavButtonsToDisplay, totalSlides),
    );
  }, [selectedIndex, scrollSnaps, numberOfNavButtonsToDisplay]);

  return (
    <div
      ref={forwardedRef}
      className={cn(
        "absolute bottom-2 left-2/4 flex -translate-x-2/4 items-center gap-0.5",
        {
          "w-full px-2": carouselType === "view-3",
        },
      )}
    >
      {relevantNavButtons.map((i) => (
        <CarouselNavigationButton
          key={i}
          onClick={() => onNavigationButtonClick(i)}
          selected={i === selectedIndex}
          carouselType={carouselType}
        />
      ))}
    </div>
  );
});

CarouselNavigation.displayName = "CarouselNavigation";

export default CarouselNavigation;
